/* eslint-disable react-hooks/exhaustive-deps */
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import {
    addDoc,
    collection,
    deleteDoc,
    doc,
    getDoc,
    getDocs,
    onSnapshot,
    orderBy,
    query,
    serverTimestamp,
    updateDoc,
    where,
} from "firebase/firestore";
import { auth, db } from "../../../firebase";
import { Devoir } from "./devoir.type";
import { useLocationState } from "../../Utils";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";


export const DevoirApi = createApi({
    reducerPath: "devoirApi",
    tagTypes: ["devoir", "devoirs", 'devoirsByStudent'],
    baseQuery: fakeBaseQuery(),
    endpoints: (builder) => ({

        addOrEditDevoir: builder.mutation({
            async queryFn({ id, ...data }) {
                try {
                    const user = auth.currentUser;
                    if (!user) {
                        return { error: { message: "Aucun utilisateur connecté." } };
                    }
                    const userId = user?.uid;
                    if (!id) {

                        const students: string[] = data.students ?? [];
                        const { students: _, ...dataWithoutStudents } = data;


                        // Cas 1 : pas d'étudiant → création simple
                        if (students.length === 0) {
                            const docRef = await addDoc(collection(db, "devoirs"), {
                                ...data,
                                createdAt: serverTimestamp(),
                                createdBy: userId,
                                updatedAt: serverTimestamp(),
                            });

                            return {
                                data: {
                                    id: docRef.id,
                                    message: "Devoir créé !",
                                    ...data,
                                },
                            };
                        }

                        // Cas 2 : avec étudiants → créer le devoir puis les devoirStudents
                        const docRef = await addDoc(collection(db, "devoirs"), {
                            ...data,
                            createdAt: serverTimestamp(),
                            createdBy: userId,
                            updatedAt: serverTimestamp(),
                        });

                        const docRefs = await Promise.all(
                            students.map((studentId: string) =>
                                addDoc(collection(db, "devoirStudents"), {
                                    studentId,
                                    devoirId: docRef.id,
                                    matiereId: dataWithoutStudents.matiere,
                                    status: dataWithoutStudents.status || "Non fait",
                                    createdAt: serverTimestamp(),
                                    createdBy: userId,
                                    updatedAt: serverTimestamp(),
                                })
                            )
                        );

                        return {
                            data: {
                                ids: docRefs.map((ref) => ref.id),
                                message: "Devoir créé !",
                                ...data,
                            },
                        };

                    } else {
                        // Mettre à jour Firestore
                        const students: string[] = data.students ?? [];
                        const { students: _, ...dataWithoutStudents } = data;
                        // 
                        console.log('dataWithoutStudents: ', dataWithoutStudents)

                        //
                        const docRef = doc(db, "devoirs", id);
                        await updateDoc(docRef, {
                            ...data,
                            updatedAt: serverTimestamp(),
                        });

                        // 2. Récupérer les devoirStudents existants pour cet objectif
                        const osQuery = query(
                            collection(db, "devoirStudents"),
                            where("devoirId", "==", id)
                        );
                        const osSnapshot = await getDocs(osQuery);

                        // IDs des étudiants déjà liés à cet objectif
                        const existingStudentIds = osSnapshot.docs.map(
                            (d) => d.data().studentId as string
                        );

                        // 3. Ajouter les nouveaux étudiants qui n'existent pas encore
                        const studentsToAdd = students.filter(
                            (studentId) => !existingStudentIds.includes(studentId)
                        );

                        // 4. Supprimer les étudiants qui ont été retirés
                        const studentsToRemove = osSnapshot.docs.filter((d) =>
                            !students.includes(d.data().studentId)
                        );

                        await Promise.all([
                            // Ajout des nouveaux
                            ...studentsToAdd.map((studentId) =>
                                addDoc(collection(db, "devoirStudents"), {
                                    studentId,
                                    devoirId: id,
                                    matiereId: dataWithoutStudents.matiere,
                                    status: "Non fait",
                                    createdAt: serverTimestamp(),
                                    createdBy: userId,
                                    updatedAt: serverTimestamp(),
                                })
                            ),
                            // Suppression des retirés
                            ...studentsToRemove.map((d) =>
                                deleteDoc(doc(db, "devoirStudents", d.id))
                            ),
                        ]);


                        return {
                            data: {
                                message:
                                    "Devoir modifié !",
                            },
                        };
                    }
                } catch (error: any) {
                    return { error: { status: "CUSTOM", message: error.message } };
                }
            },
            invalidatesTags: ["devoir", "devoirs"],
        }),
        getDevoirs: builder.query({
            async queryFn() {
                try {
                    return new Promise((resolve) => {
                        const q = query(
                            collection(db, "devoirs"),
                            orderBy("createdAt", "desc"),
                        );
                        onSnapshot(q, (snapshot) => {
                            const Devoirs = snapshot.docs.map((doc) => ({
                                id: doc.id,
                                ...doc.data(),
                            }));
                            resolve({ data: Devoirs as Devoir[] });
                        });
                        // unsubscribe();
                    });
                } catch (error) {
                    return { error };
                }
            },
            keepUnusedDataFor: 0,
            async onCacheEntryAdded(
                arg,
                { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
            ) {
                const DevoirsRef = query(
                    collection(db, "devoirs"),
                    orderBy("createdAt", "desc"),
                );
                let unsubscribe;
                try {
                    await cacheDataLoaded;

                    unsubscribe = onSnapshot(DevoirsRef, (snapshot) => {
                        const Devoirs = snapshot.docs.map((doc) => ({
                            id: doc.id,
                            ...doc.data(),
                        }));
                        updateCachedData(() => Devoirs as Devoir[]);
                    });
                } catch (error) {
                    console.error("Erreur de souscription:", error);
                }

                // Nettoyer l'écouteur Firestore quand le cache est supprimé
                await cacheEntryRemoved;
                if (unsubscribe) unsubscribe();
            },
            providesTags: ["devoirs"],
        }),
        getDevoirsByStudent: builder.query({
            // queryFn simple, juste pour initialiser le cache
            async queryFn(studentId: string) {

                if (!studentId) return { error: { message: "studentId manquant" } };

                try {
                    const user = auth.currentUser;


                    if (!user) return { error: { message: "Aucun utilisateur connecté." } };


                    const q = query(
                        collection(db, "devoirs"),
                        where("createdBy", "==", user.uid),
                        where("students", "array-contains", studentId),
                        orderBy("createdAt", "desc")
                    );


                    const snapshot = await getDocs(q); // ← getDocs, pas onSnapshot ici


                    const devoirs = snapshot.docs.map((doc) => ({
                        uid: doc.id,
                        ...doc.data(),
                    })) as any[];


                    const devoirsWithMatiere = await Promise.all(
                        devoirs.map(async (devoir) => {
                            if (!devoir.matiere) return devoir;
                            try {
                                const matiereRef = doc(db, "matieres", devoir.matiere);
                                const matiereSnap = await getDoc(matiereRef);
                                return {
                                    ...devoir,
                                    matiere: matiereSnap.exists()
                                        ? { uid: matiereSnap.id, ...matiereSnap.data() }
                                        : devoir.matiere,
                                };
                            } catch {
                                return devoir;
                            }
                        })
                    );

                    return { data: devoirsWithMatiere };
                } catch (error) {
                    console.error("ERREUR queryFn:", error);
                    return { error };
                }
            },

            keepUnusedDataFor: 60, // ← pas 0, laisse le temps à onCacheEntryAdded

            async onCacheEntryAdded(
                arg,
                { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
            ) {
                let unsubscribe: (() => void) | undefined;

                try {
                    await cacheDataLoaded; // attend que queryFn ait résolu

                    const user = auth.currentUser;
                    if (!user) return;

                    const q = query(
                        collection(db, "devoirs"),
                        where("createdBy", "==", user.uid),
                        where("students", "array-contains", arg),
                        orderBy("createdAt", "desc")
                    );

                    unsubscribe = onSnapshot(q, async (snapshot) => {
                        const devoirs = snapshot.docs.map((d) => ({
                            uid: d.id,
                            ...d.data(),
                        })) as any[];

                        const devoirsWithMatiere = await Promise.all(
                            devoirs.map(async (devoir) => {
                                if (!devoir.matiere) return devoir;
                                try {
                                    const matiereRef = doc(db, "matieres", devoir.matiere);
                                    const matiereSnap = await getDoc(matiereRef);
                                    return {
                                        ...devoir,
                                        matiere: matiereSnap.exists()
                                            ? { uid: matiereSnap.id, ...matiereSnap.data() }
                                            : devoir.matiere,
                                    };
                                } catch {
                                    return devoir;
                                }
                            })
                        );

                        updateCachedData(() => devoirsWithMatiere);
                    });

                } catch {
                    // cacheDataLoaded rejeté → silencieux
                }

                await cacheEntryRemoved;
                if (unsubscribe) unsubscribe(); // nettoyage du listener
            },

            providesTags: ["devoirsByStudent"],
        }),
        getDevoirByUser: builder.query({
            queryFn: async () => {
                try {
                    const user = auth.currentUser;
                    if (!user) return { error: { message: "Aucun utilisateur connecté." } };

                    const userId = user.uid;
                    const devoirsRef = collection(db, "devoirs");
                    const q = query(devoirsRef, where("createdBy", "==", userId), orderBy("createdAt", "desc"));

                    const snapshot = await getDocs(q);
                    const devoirsRaw = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Devoir[];


                    const allStudentIds = Array.from(new Set(devoirsRaw.flatMap(d => d.students || [])));
                    const allMatiereIds = Array.from(new Set(devoirsRaw.map(d => d.matiere).filter(Boolean)));

                    if (devoirsRaw.length === 0) return { data: [] };


                    const studentsMap: Record<string, any> = {};
                    const matieresMap: Record<string, any> = {};


                    const studentChunks = [];
                    for (let i = 0; i < allStudentIds.length; i += 30) {
                        studentChunks.push(allStudentIds.slice(i, i + 30));
                    }

                    const studentPromises = studentChunks.map(chunk =>
                        getDocs(query(collection(db, "users"), where("uid", "in", chunk)))
                    );


                    let matierePromise = Promise.resolve() as any;
                    if (allMatiereIds.length > 0) {
                        matierePromise = getDocs(query(collection(db, "matieres"), where("__name__", "in", allMatiereIds)));
                    }

                    const [studentSnapshots, matiereSnapshot] = await Promise.all([
                        Promise.all(studentPromises),
                        matierePromise
                    ]);


                    studentSnapshots.forEach(snap => snap.forEach(doc => studentsMap[doc.id] = { id: doc.id, ...doc.data() }));
                    if (matiereSnapshot) {
                        matiereSnapshot.forEach((doc: any) => matieresMap[doc.id] = { id: doc.id, ...doc.data() });
                    }


                    const finalData = devoirsRaw.map(devoir => ({
                        ...devoir,
                        matiereData: matieresMap[devoir.matiere] || devoir.matiere,
                        studentsData: (devoir.students || []).map(uid => studentsMap[uid]).filter(Boolean)
                    }));

                    return { data: finalData };
                } catch (error: any) {
                    return { error: error.message };
                }
            },

            async onCacheEntryAdded(arg, { updateCachedData, cacheDataLoaded, cacheEntryRemoved }) {
                const user = auth.currentUser;
                if (!user) return;

                let unsubscribe: any;
                try {
                    await cacheDataLoaded;
                    const q = query(collection(db, "devoirs"), where("createdBy", "==", user.uid), orderBy("createdAt", "desc"));

                    unsubscribe = onSnapshot(q, async (snapshot) => {
                        const devoirsRaw = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Devoir[];

                        const allStudentIds = Array.from(new Set(devoirsRaw.flatMap(d => d.students || [])));
                        const allMatiereIds = Array.from(new Set(devoirsRaw.map(d => d.matiere).filter(Boolean)));


                        const studentsMap: Record<string, any> = {};
                        const matieresMap: Record<string, any> = {};

                        const studentChunks = [];
                        for (let i = 0; i < allStudentIds.length; i += 30) studentChunks.push(allStudentIds.slice(i, i + 30));

                        const studentPromises = studentChunks.map(chunk => getDocs(query(collection(db, "users"), where("uid", "in", chunk))));

                        let matierePromise = allMatiereIds.length > 0
                            ? getDocs(query(collection(db, "matieres"), where("__name__", "in", allMatiereIds)))
                            : Promise.resolve(null);

                        const [studentSnaps, matiereSnap] = await Promise.all([Promise.all(studentPromises), matierePromise]);

                        studentSnaps.forEach(snap => snap.forEach(doc => studentsMap[doc.id] = { id: doc.id, ...doc.data() }));
                        if (matiereSnap) matiereSnap.forEach((doc: any) => matieresMap[doc.id] = { id: doc.id, ...doc.data() });

                        updateCachedData(() => {
                            return devoirsRaw.map(devoir => ({
                                ...devoir,
                                matiereData: matieresMap[devoir.matiere] || devoir.matiere,
                                studentsData: (devoir.students || []).map(uid => studentsMap[uid]).filter(Boolean)
                            })) as any[];
                        });
                    });
                } catch (error) {
                    console.error(error);
                }

                await cacheEntryRemoved;
                if (unsubscribe) unsubscribe();
            },
            providesTags: ["devoirs"],
        }),
        getDevoirById: builder.query({
            queryFn: async (DevoirId) => {
                try {
                    const DevoirRef = doc(db, "devoirs", DevoirId);
                    const DevoirSnap = await getDoc(DevoirRef);

                    if (!DevoirSnap.exists()) {
                        return {
                            error: { status: 404, message: "Devoir introuvable !" },
                        };
                    }

                    return { data: { id: DevoirSnap.id, ...DevoirSnap.data() } };
                } catch (error: any) {
                    return { error: { status: 500, message: error.message } };
                }
            },
            providesTags: ["devoir"],
        }),
        assignDevoir: builder.mutation({
            async queryFn({ id, ...data }) {
                try {
                    const user = auth.currentUser;
                    if (!user) {
                        return { error: { message: "Aucun utilisateur connecté." } };
                    }
                    const userId = user.uid;

                    const students: string[] = Array.from(new Set(data.students ?? []));
                    const { students: _, ...dataWithoutStudents } = data;

                    // 1. Mettre à jour le devoir avec la liste finale (source de vérité)
                    const docRef = doc(db, "devoirs", id);
                    await updateDoc(docRef, {
                        ...dataWithoutStudents,
                        students,
                        updatedAt: serverTimestamp(),
                    });

                    // 2. Récupérer les devoirStudents existants pour ce devoir
                    const devoirStudentsQuery = query(
                        collection(db, "devoirStudents"),
                        where("devoirId", "==", id)
                    );
                    const devoirStudentsSnapshot = await getDocs(devoirStudentsQuery);

                    const existingStudentIds = devoirStudentsSnapshot.docs.map(
                        (d) => d.data().studentId as string
                    );

                    // 3. Nouveaux élèves à ajouter
                    const studentsToAdd = students.filter(
                        (studentId) => !existingStudentIds.includes(studentId)
                    );

                    // 4. Élèves retirés à supprimer
                    const studentsToRemove = devoirStudentsSnapshot.docs.filter(
                        (d) => !students.includes(d.data().studentId)
                    );

                    await Promise.all([
                        ...studentsToAdd.map((studentId) =>
                            addDoc(collection(db, "devoirStudents"), {
                                studentId,
                                devoirId: id,
                                matiereId: dataWithoutStudents.matiere,
                                status: "Non fait",
                                createdAt: serverTimestamp(),
                                createdBy: userId,
                                updatedAt: serverTimestamp(),
                            })
                        ),
                        ...studentsToRemove.map((d) =>
                            deleteDoc(doc(db, "devoirStudents", d.id))
                        ),
                    ]);

                    return { data: { message: "Devoir attribué !" } };
                } catch (error: any) {
                    console.log("ERROR API", error);
                    return { error: { status: "CUSTOM", message: error.message } };
                }
            },
            invalidatesTags: ["devoir", "devoirs", "devoirsByStudent"],
        }),
        // Supprimer
        deleteDevoir: builder.mutation({
            queryFn: async (uid) => {
                try {
                    const docRef = doc(db, "devoirs", uid);
                    await deleteDoc(docRef);
                    return {
                        data: { uid, message: "Devoir supprimé !", success: true },
                    };
                } catch (error: any) {
                    return { error: { status: "DELETE_ERROR", message: error.message } };
                }
            },
            invalidatesTags: ["devoir", "devoirs"],
        }),
    }),
});

export const {
    useAddOrEditDevoirMutation,
    useGetDevoirsQuery,
    useGetDevoirByUserQuery,
    useDeleteDevoirMutation,
    useLazyGetDevoirByIdQuery,
    useGetDevoirsByStudentQuery,
    useAssignDevoirMutation,
} = DevoirApi;

export function useDevoirFromLocation(): [
    Devoir,
    boolean,
    string,
    (id: string) => any
] {
    const itemState = useLocationState<Devoir | undefined>(undefined);
    const [item, setItem] = useState(itemState);
    const { id } = useParams<{ id: string }>();
    const [findById, { data, isError, isLoading }] = useLazyGetDevoirByIdQuery();
    const navigate = useNavigate();

    useEffect(() => {
        // console.log("is id", id)
        if (id && id !== undefined) {
            // console.log("id", id)
            findById(id as string);
        }
    }, [id]);
    useEffect(() => {
        if (isError && !itemState) {
            navigate(-1);
        }
    }, [isError]);
    useEffect(() => {
        if (data) {
            setItem(data as Devoir);
        }
    }, [data]);

    return [item as Devoir & { bio: string }, isLoading, id as string, findById];
}